"use client";

import BillingPeriodSelector from "@/app/components/membership/BillingPeriodSelector";
import CurrencySelector from "@/app/components/membership/CurrencySelector";
import {
  getPriceForBillingPeriod,
  getUnitAmountFromPrice,
} from "@/lib/utils/subscription";
import { StripeProductMetadata } from "@/types/product-schema";
import { Box, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { CreateMembershipFormSubjectMeta } from "../../register/membership/page";
import PricingCard from "./PricingCard";
import classes from "./pricing-section.module.css";

export default function PricingSection({
  stripeProducts,
  subjectMeta,
}: {
  stripeProducts: StripeProductMetadata;
  subjectMeta: CreateMembershipFormSubjectMeta;
}) {
  const [selectedCurrency, setSelectedCurrency] = useState<string>("usd");
  const [selectedBillingPeriod, setSelectedBillingPeriod] =
    useState<string>("year-2");

  const allAccessProduct = stripeProducts.allAccessProduct;
  const singleSubjectProduct = stripeProducts.subjectProducts[0];

  const allAccessPrice = useMemo(() => {
    try {
      return getPriceForBillingPeriod(
        allAccessProduct.prices,
        selectedCurrency,
        selectedBillingPeriod
      );
    } catch {
      return null;
    }
  }, [allAccessProduct.prices, selectedBillingPeriod, selectedCurrency]);

  const perSubjectPrice = useMemo(() => {
    if (!singleSubjectProduct) return null;
    try {
      return getPriceForBillingPeriod(
        singleSubjectProduct.prices,
        selectedCurrency,
        selectedBillingPeriod
      );
    } catch {
      return null;
    }
  }, [singleSubjectProduct, selectedBillingPeriod, selectedCurrency]);

  const allAccessAmount = allAccessPrice
    ? getUnitAmountFromPrice(allAccessPrice)
    : 0;

  const perSubjectAmount = perSubjectPrice
    ? getUnitAmountFromPrice(perSubjectPrice)
    : 0;

  return (
    <Stack className={classes.container}>
      {/* Currency and Billing Period Selectors */}
      <Box className={classes.filtersWrapper}>
        <CurrencySelector
          selectedCurrency={selectedCurrency}
          onCurrencyChange={setSelectedCurrency}
          referencePrices={singleSubjectProduct.prices}
        />
        <BillingPeriodSelector
          selectedPeriod={selectedBillingPeriod}
          onPeriodChange={setSelectedBillingPeriod}
          referencePrices={singleSubjectProduct.prices}
        />
      </Box>

      {/* Membership Cards */}
      <Box className={classes.cardsContainer}>
        <PricingCard
          title="All-Access Plan"
          description="Best for serious students who want to excel across the entire curriculum."
          price={allAccessAmount}
          currency={selectedCurrency}
          billingPeriod={selectedBillingPeriod}
          isAllAccess={true}
          subjectMeta={subjectMeta}
        />

        <PricingCard
          title="Per-Subject Plan"
          description="Perfect for focusing your efforts on specific subjects that need extra attention."
          price={perSubjectAmount}
          currency={selectedCurrency}
          billingPeriod={selectedBillingPeriod}
          isAllAccess={false}
          subjectMeta={subjectMeta}
        />
      </Box>
    </Stack>
  );
}
